import { useState, useCallback, useMemo } from "react";
import {
  Card,
  BlockStack,
  Text,
  InlineStack,
  Box,
  Button,
  Icon,
  SkeletonBodyText,
  InlineGrid,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {
  LocationIcon,
  PlusCircleIcon,
  PlusIcon,
  XIcon,
  TeamIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";

import useEventMetadata from "@features/events/hooks/useEventMetadata";
import { Event, EventRelation } from "@features/events/types";
import SelectRelatedEventsModal from "@features/events/pages/events/components/related/SelectRelatedEventsModal";
import EventBadgeRenderer from "@features/events/components/EventBadgeRenderer";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { RenderDate } from "@shared/ui";
import { formatStartDate } from "@shared/lib";
import { getLocalizedText } from "@shared/lib";
import { useVenues } from "@features/events/hooks/useVenues";
import { Venue } from "@features/events/types";
import IconWithValueAndTooltip from "@features/events/components/common/IconWithValueAndTooltip";
import InstructorsAvatarGroup from "@features/events/components/InstructorsAvatarGroup";

interface RelatedEventsCardProps {
  eventData: Partial<Event>;
  onDataChange: (updates: Partial<Event>) => void;
}

const RelatedEventItem: React.FC<{
  onRemove: () => void;
  event: Event;
  venues: Venue[];
  loading: boolean;
  hcc: string;
  allEventsInGroup: EventRelation[];
  currentEvent: EventRelation;
}> = ({
  onRemove,
  event,
  venues,
  loading,
  hcc,
  allEventsInGroup,
  currentEvent,
}) => {
  const { t } = useTranslation();

  const title =
    getLocalizedText(event?.title) ||
    "";

  const currentIndex = allEventsInGroup.findIndex(
    (e) => e.id === currentEvent.id && e.relation === currentEvent.relation
  );
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === allEventsInGroup.length - 1;

  const borderRadiusStyle = {
    borderTopLeftRadius: isFirst ? "var(--p-border-radius-200)" : undefined,
    borderTopRightRadius: isFirst ? "var(--p-border-radius-200)" : undefined,
    borderBottomLeftRadius: isLast ? "var(--p-border-radius-200)" : undefined,
    borderBottomRightRadius: isLast ? "var(--p-border-radius-200)" : undefined,
    overflow: "hidden",
  };

  // Don't render if event is null/undefined
  if (!event) {
    return null;
  }

  if (loading) {
    return <SkeletonBodyText lines={1} />;
  }

  const venueId = event.venueIds?.[0];
  const venue = venues.find((v) => v.id === venueId);
  const eventUrl = `/e/${hcc}/events/${event.id}`;

  return (
    <div style={borderRadiusStyle}>
      <SortableResourceItem
        dragHandleAccessibilityLabel={`Reorder ${title || "event"}`}
      >
        <InlineGrid
          columns={{ sm: 1, md: 2, lg: "1fr auto auto auto auto auto auto" }}
          gap={{ sm: "200", md: "400", lg: "300" }}
        >
          <InlineStack align="start" blockAlign="center">
            <BlockStack gap="200">
              <a
                href={eventUrl}
                aria-label={`View details for ${title || "event"}`}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                <Text as="span" variant="bodyMd" fontWeight="bold" truncate>
                  {title}
                </Text>
              </a>
              <InlineStack gap="100" align="start" blockAlign="center" wrap={false}>
                <Box>
                  <Icon source={LocationIcon} tone="subdued" />
                </Box>
                <Text as="span" variant="bodySm" tone="subdued">
                  {event.dateStart === event.dateEnd && event.dateStart ? (
                    <RenderDate dayFirst={true} date={event.dateStart} />
                  ) : (
                    <>
                      {formatStartDate(event.dateStart)}
                      {event.dateEnd && (
                        <>
                          {" "}
                          - <RenderDate dayFirst={true} date={event.dateEnd} />
                        </>
                      )}
                    </>
                  )}
                </Text>
                <Box>
                  <Icon source={LocationIcon} tone="subdued" />
                </Box>
                <Text as="span" variant="bodySm" tone="subdued">
                  {getLocalizedText(venue?.geoAddress?.summary)}
                </Text>
              </InlineStack>
            </BlockStack>
          </InlineStack>

          <InlineStack align="end" blockAlign="center">
            <Box>
              <InstructorsAvatarGroup
                instructorIds={event.instructorIds}
                maxShow={5}
                size="small"
              />
            </Box>
          </InlineStack>

          <InlineStack align="end" blockAlign="center">
            <Box>
              <EventBadgeRenderer slug={event.status ?? ""} entity="event" />
            </Box>
          </InlineStack>

          <InlineStack align="end" blockAlign="center">
            <Box>
              <EventBadgeRenderer
                slug={event.visibility ?? 0}
                entity="visibility"
              />
            </Box>
          </InlineStack>

          <InlineStack align="end" blockAlign="center">
            <Box>
              <IconWithValueAndTooltip
                iconSource={TeamIcon}
                value={event.spotsCurrent ?? 0}
                tooltip={t("eventDashboard.registered")}
                iconTone="subdued"
                textTone="subdued"
                alignment="end"
              />
            </Box>
          </InlineStack>

          <InlineStack align="end" blockAlign="center">
            <Box>
              <Button
                icon={XIcon}
                tone="critical"
                variant="tertiary"
                onClick={() => onRemove()}
                accessibilityLabel={t("eventDetails.relatedEvents.removeEvent")}
              />
            </Box>
          </InlineStack>
        </InlineGrid>
      </SortableResourceItem>
    </div>
  );
};

export default function RelatedEventsCard({
  eventData,
  onDataChange,
}: RelatedEventsCardProps) {
  const { t } = useTranslation();
  const { metadata } = useEventMetadata();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRelation, setSelectedRelation] = useState("");
  const { GET } = useApiRequest();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const { data: allEvents = [], isLoading: allEventsLoading } = useQuery<
    Event[]
  >({
    queryKey: ["events", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/events?`),
    enabled: Boolean(hcc),
    placeholderData: keepPreviousData,
  });
  const relatedEvents = useMemo(() => {
    return (eventData.relatedEvents || []).sort((a, b) => {
      return a.relation.localeCompare(b.relation);
    });
  }, [eventData.relatedEvents]);

  const existingEventIds = relatedEvents
    .filter((re) => re.relation === selectedRelation)
    .map((re) => re.id);

  const eventIdsInOtherRelations = relatedEvents
    .filter((re) => re.relation !== selectedRelation)
    .map((re) => re.id);

  const handleAddEvents = useCallback(
    (eventIds: string[], relation: string) => {
      const newRelations: EventRelation[] = eventIds.map((id) => ({
        id,
        relation,
      }));
      const otherRelations = relatedEvents.filter(
        (re) => re.relation !== relation
      );
      const updatedRelatedEvents = [...otherRelations, ...newRelations];
      onDataChange({ relatedEvents: updatedRelatedEvents });
    },
    [relatedEvents, onDataChange]
  );

  const handleRemoveEvent = useCallback(
    (eventId: string, relation: string) => {
      const updatedRelatedEvents = relatedEvents.filter(
        (re) => !(re.id === eventId && re.relation === relation)
      );
      onDataChange({ relatedEvents: updatedRelatedEvents });
    },
    [relatedEvents, onDataChange]
  );

  const handleReorderEvents = useCallback(
    (relation: string, reorderedEvents: EventRelation[]) => {
      const otherEvents = relatedEvents.filter(
        (re) => re.relation !== relation
      );
      const updatedRelatedEvents = [...otherEvents, ...reorderedEvents];
      onDataChange({ relatedEvents: updatedRelatedEvents });
    },
    [relatedEvents, onDataChange]
  );

  const handleAddToGroup = useCallback((relation: string) => {
    setSelectedRelation(relation);
    setModalOpen(true);
  }, []);

  // Group events by relation type
  const groupedEvents = useMemo(() => {
    const groups: Record<string, EventRelation[]> = {};
    relatedEvents.forEach((event) => {
      if (!groups[event.relation]) {
        groups[event.relation] = [];
      }
      groups[event.relation].push(event);
    });
    return groups;
  }, [relatedEvents]);

  const relationLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    metadata?.eventRelations?.forEach((relation) => {
      labels[relation.slug] = relation.label;
    });
    return labels;
  }, [metadata?.eventRelations]);

  const { data: venues = [], isLoading: venuesLoading } = useVenues({ hcc });

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text variant="headingMd" as="h2">
            {t("eventDetails.relatedEvents.title")}
          </Text>
          {relatedEvents.length > 0 && (
            <Button
              icon={PlusCircleIcon}
              onClick={() => {
                setSelectedRelation("");
                setModalOpen(true);
              }}
            >
              {t("eventDetails.relatedEvents.addEvent")}
            </Button>
          )}
        </InlineStack>

        <Text as="p" tone="subdued">
          {t("eventDetails.relatedEvents.emptyDescription")}
        </Text>

        {Object.keys(groupedEvents).length === 0 ? (
          <Box paddingBlock="800">
            <BlockStack gap="300" align="center" inlineAlign="center">
              <Text variant="bodyMd" as="h3" tone="subdued">
                {t("eventDetails.relatedEvents.noEventsLinked")}
              </Text>
              <Button
                icon={PlusIcon}
                variant="primary"
                onClick={() => {
                  setSelectedRelation("");
                  setModalOpen(true);
                }}
              >
                {t("eventDetails.relatedEvents.addEvent")}
              </Button>
            </BlockStack>
          </Box>
        ) : (
          <BlockStack gap="400">
            {Object.entries(groupedEvents).map(([relation, events]) => (
              <BlockStack key={relation} gap="200">
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="headingSm" as="h3">
                    {relationLabels[relation] || relation}
                  </Text>
                </InlineStack>

                <BlockStack gap="200">
                  <div
                    style={{
                      border: "1px solid var(--p-color-border)",
                      borderRadius: "var(--p-border-radius-200)",
                      backgroundColor: "var(--p-color-bg-surface)",
                      overflow: "hidden",
                    }}
                  >
                    <div style={{ margin: "-8px 0" }}>
                      <SortableList
                        items={events}
                        idForItem={(item: EventRelation) =>
                          `${item?.id}-${item?.relation}`
                        }
                        onReorderEnd={(reorderedEvents) =>
                          handleReorderEvents(relation, reorderedEvents)
                        }
                        showDividers={false}
                        renderItem={(event) => {
                          const foundEvent = allEvents.find((e) => e.id === event.id);
                          // Skip rendering if event is not found in allEvents
                          if (!foundEvent) {
                            return null;
                          }
                          return (
                            <RelatedEventItem
                              venues={venues}
                              loading={venuesLoading || allEventsLoading}
                              event={foundEvent}
                              onRemove={() =>
                                handleRemoveEvent(event.id, event.relation)
                              }
                              hcc={hcc ?? ""}
                              allEventsInGroup={events}
                              currentEvent={event}
                            />
                          );
                        }}
                      />
                    </div>
                  </div>
                  <InlineStack align="start" blockAlign="center">
                    <Button
                      icon={PlusCircleIcon}
                      variant="plain"
                      size="slim"
                      onClick={() => handleAddToGroup(relation)}
                    >
                      {t("eventDetails.relatedEvents.addEvent")}
                    </Button>
                  </InlineStack>
                </BlockStack>
              </BlockStack>
            ))}
          </BlockStack>
        )}

        <SelectRelatedEventsModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedRelation("");
          }}
          onAdd={handleAddEvents}
          existingRelatedEventIds={existingEventIds}
          eventIdsInOtherRelations={eventIdsInOtherRelations}
          preselectedRelation={selectedRelation}
        />
      </BlockStack>
    </Card>
  );
}
